import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { useToast } from './Toast'
import { Calendar, Copy, Check, X, ExternalLink } from 'lucide-react'

/**
 * Calendar Link Modal Component
 * Toont calendar feed link en instructies voor synchronisatie
 */
function CalendarLinkModal({ isOpen, onClose }) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)

  // Haal calendar token op
  const { data: calendarData, isLoading } = useQuery({
    queryKey: ['calendar-token'],
    queryFn: async () => {
      const response = await api.get('/planning/calendar/my-token')
      return response.data
    },
    enabled: isOpen,
  })

  // Genereer nieuwe token mutation
  const generateTokenMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/planning/calendar/generate-token')
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['calendar-token'])
      toast.success(t('dialogs.calendarLink.nieuweLinkGegenereerd', 'Nieuwe calendar link gegenereerd'))
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('dialogs.calendarLink.foutGenereren', 'Fout bij genereren link'))
    },
  })

  const handleCopy = async () => {
    if (!calendarData?.feed_url) return

    try {
      // Converteer relatief pad naar absolute URL
      const absoluteUrl = calendarData.feed_url.startsWith('http') 
        ? calendarData.feed_url 
        : `${window.location.origin}${calendarData.feed_url}`
      
      await navigator.clipboard.writeText(absoluteUrl)
      setCopied(true)
      toast.success(t('dialogs.calendarLink.linkGekopieerd', 'Link gekopieerd naar klembord'))
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      toast.error(t('dialogs.calendarLink.konNietKopieren', 'Kon link niet kopiëren'))
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('dialogs.calendarLink.title', 'Agenda Synchronisatie')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Link Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('dialogs.calendarLink.uwAgendaLink', 'Uw Agenda Link')}
            </label>
            {isLoading ? (
              <div className="text-gray-500">{t('common.loading', 'Laden...')}</div>
            ) : calendarData?.feed_url ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={calendarData.feed_url.startsWith('http') 
                    ? calendarData.feed_url 
                    : `${window.location.origin}${calendarData.feed_url}`}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                />
                <button
                  onClick={handleCopy}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    copied
                      ? 'bg-green-600 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 inline mr-2" />
                      {t('dialogs.calendarLink.gekopieerd', 'Gekopieerd!')}
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 inline mr-2" />
                      {t('dialogs.calendarLink.kopieer', 'Kopieer')}
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="text-gray-500">{t('dialogs.calendarLink.geenLink', 'Geen link beschikbaar')}</div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
            <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-3">
              {t('dialogs.calendarLink.hoeSynchroniseer', 'Hoe synchroniseer ik mijn agenda?')}
            </h3>
            <div className="space-y-3 text-sm text-blue-800 dark:text-blue-200">
              <div>
                <strong>{t('dialogs.calendarLink.googleCalendar', 'Google Calendar:')}</strong>
                <ol className="list-decimal list-inside ml-4 mt-1 space-y-1">
                  <li>Open Google Calendar</li>
                  <li>Klik op het "+" naast "Andere agenda's"</li>
                  <li>Selecteer "Via URL toevoegen"</li>
                  <li>Plak de bovenstaande link</li>
                  <li>Klik op "Agenda toevoegen"</li>
                </ol>
              </div>
              <div>
                <strong>{t('dialogs.calendarLink.outlook', 'Outlook:')}</strong>
                <ol className="list-decimal list-inside ml-4 mt-1 space-y-1">
                  <li>Open Outlook Calendar</li>
                  <li>Ga naar "Bestand" → "Accountinstellingen" → "Accountinstellingen"</li>
                  <li>Klik op "Internetagenda's" tab</li>
                  <li>Klik op "Nieuw"</li>
                  <li>Plak de bovenstaande link</li>
                </ol>
              </div>
              <div>
                <strong>{t('dialogs.calendarLink.appleCalendar', 'Apple Calendar:')}</strong>
                <ol className="list-decimal list-inside ml-4 mt-1 space-y-1">
                  <li>Open Calendar app</li>
                  <li>Ga naar "Bestand" → "Nieuwe agenda-abonnement"</li>
                  <li>Plak de bovenstaande link</li>
                  <li>Klik op "Abonneren"</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <strong>{t('dialogs.calendarLink.watGesynchroniseerd', 'Wat wordt gesynchroniseerd?')}</strong>
              <br />
              {t('dialogs.calendarLink.watGesynchroniseerdDesc', 'Uw agenda wordt automatisch bijgewerkt met:')}
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('dialogs.calendarLink.takenToegewezen', 'Alle taken die aan u zijn toegewezen')}</li>
                <li>{t('dialogs.calendarLink.montagePlanning', 'Montage planningen waar u bij betrokken bent')}</li>
                <li>{t('dialogs.calendarLink.werkbonnen', 'Werkbonnen waar u aan werkt')}</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t dark:border-gray-700">
            <button
              onClick={() => generateTokenMutation.mutate()}
              disabled={generateTokenMutation.isPending}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              {generateTokenMutation.isPending ? t('common.loading', 'Laden') + '...' : t('dialogs.calendarLink.genereerNieuweLink', 'Genereer nieuwe link')}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('dialogs.common.close', 'Sluiten')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CalendarLinkModal
